import { useMemo, useState } from 'react';
import { Empty, Flex, Input, Skeleton, theme, Tree } from 'antd';
import { FiSearch } from 'react-icons/fi';
import styled from 'styled-components';

import { collectUuids, filterObjectTree } from '@utils/taxonomyTree';

// Nodes are identified by uuid; the backend `key` is a slug, not the tree id.
const TREE_FIELD_NAMES = { key: 'uuid', title: 'name' };

// Card rows are taller than antd's single-line nodes: space the rows apart and
// center the caret on the card.
const treeStyles = gap => ({
	item: { marginBottom: gap },
	itemSwitcher: { alignSelf: 'center' },
});

// A flex item will not shrink below its content by default, and a card's one-line
// description is as wide as its text: without this the card spills out of a narrow
// column instead of ellipsizing. The content wrapper has no semantic `styles` key,
// so this is tied to an antd v6 class name — review on upgrades.
const TreeWrapper = styled.div`
	.ant-tree-node-content-wrapper {
		min-width: 0;
	}
`;

const DEFAULT_MESSAGES = {
	error: "Couldn't load objects. Please try again.",
	empty: 'No objects yet.',
	noMatch: 'No objects match this search.',
};

const ListMessage = ({ children }) => (
	<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={children} />
);

/**
 * A searchable taxonomy tree whose rows are cards: the Objects page list, and
 * the object picker of annotation mode. The search matches a name or a system
 * node's alias and keeps each match in context (its ancestors and descendants
 * stay), revealing every match without losing what the user had open.
 *
 * Owns only the search and expand state. The caller fetches the tree (see
 * `useObjects`) and says what one row looks like.
 *
 * @param {Array} props.tree - nested nodes from `buildObjectTree`
 * @param {(node: object) => import('react').ReactNode} props.renderNode - the card for one node
 * @param {boolean} [props.isLoading]
 * @param {boolean} [props.isError]
 * @param {string|null} [props.selectedUuid] - the row drawn as selected
 * @param {(uuid: string, node: object) => void} [props.onSelect] - a click on a
 *   row; clicking the selected row again reports it again rather than clearing it
 * @param {string} [props.searchPlaceholder] - doubles as the search field's label
 * @param {{ error?: string, empty?: string, noMatch?: string }} [props.messages]
 */
const ObjectTree = ({
	tree = [],
	renderNode,
	isLoading = false,
	isError = false,
	selectedUuid = null,
	onSelect,
	searchPlaceholder = 'Search objects or aliases',
	messages = DEFAULT_MESSAGES,
}) => {
	const { token } = theme.useToken();

	const [query, setQuery] = useState('');
	// null = no manual expand/collapse yet → everything expanded.
	const [expandedKeys, setExpandedKeys] = useState(null);
	// Expand state within one search; a new query starts fully expanded again.
	const [searchKeys, setSearchKeys] = useState(null);

	const trimmedQuery = query.trim();
	const isSearching = trimmedQuery.length > 0;

	const visibleTree = useMemo(() => filterObjectTree(tree, trimmedQuery), [tree, trimmedQuery]);
	const allKeys = useMemo(() => collectUuids(visibleTree), [visibleTree]);

	// A search reveals every match, kept separate from the browsing state so
	// clearing the search restores what the user had open.
	const effectiveExpandedKeys = (isSearching ? searchKeys : expandedKeys) ?? allKeys;

	const { error, empty, noMatch } = { ...DEFAULT_MESSAGES, ...messages };

	const handleQueryChange = event => {
		setQuery(event.target.value);
		setSearchKeys(null);
	};

	const handleExpand = keys => (isSearching ? setSearchKeys(keys) : setExpandedKeys(keys));

	const handleSelect = (_keys, { node }) => onSelect?.(node.uuid, node);

	const renderBody = () => {
		if (isLoading) return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
		if (isError) return <ListMessage>{error}</ListMessage>;
		if (tree.length === 0) return <ListMessage>{empty}</ListMessage>;
		if (visibleTree.length === 0) return <ListMessage>{noMatch}</ListMessage>;

		return (
			<TreeWrapper>
				<Tree
					blockNode
					styles={treeStyles(token.marginXS)}
					fieldNames={TREE_FIELD_NAMES}
					treeData={visibleTree}
					titleRender={renderNode}
					expandedKeys={effectiveExpandedKeys}
					onExpand={handleExpand}
					selectedKeys={selectedUuid ? [selectedUuid] : []}
					onSelect={handleSelect}
				/>
			</TreeWrapper>
		);
	};

	return (
		<Flex vertical gap="small">
			<Input
				allowClear
				value={query}
				onChange={handleQueryChange}
				placeholder={searchPlaceholder}
				aria-label={searchPlaceholder}
				prefix={<FiSearch aria-hidden color={token.colorTextTertiary} />}
			/>
			{renderBody()}
		</Flex>
	);
};

export default ObjectTree;
